import { ExternalLink } from 'lucide-react';
import { tx } from '../i18n';
import { formatDate } from '../lib/format';
import { statusIcon, statusLabel, statusOfSeverity } from '../lib/status';
import { num } from '../lib/values';
import type { FindingView } from '../types/api';

/**
 * What this observation cost the category, in points.
 *
 * <p>The scorers put it in the evidence map, which is the structured half of a finding: a reader
 * who is told "single glazing recorded" cannot tell whether that is a remark or the reason the
 * score is what it is. Ten points off says which. Findings with no measurable effect - the ones
 * that describe rather than penalise - simply do not carry the number.
 */
export function scoreImpact(finding: FindingView): number | null {
  return num(finding.evidence?.scoreImpact);
}

interface FindingProps {
  finding: FindingView;
  /** The category this sits in, named in the impact line. */
  categoryLabel?: string;
}

/**
 * One evidenced finding, with how serious it is, what it cost, its source and its date.
 *
 * <p>Severity is carried by the icon, by a word, and by the colour of the left border, in that
 * order of reliability - a border colour is exactly the kind of signal that disappears in
 * greyscale or for a reader who does not separate red from amber.
 */
export function Finding({ finding, categoryLabel }: FindingProps) {
  const status = statusOfSeverity(finding.severity);
  const Icon = statusIcon(status);
  const impact = scoreImpact(finding);

  return (
    <div className={`finding finding--${status}`}>
      <Icon size={16} aria-hidden="true" className="finding__icon" />
      <div className="finding__body">
        <p className="finding__title">{finding.title}</p>

        <p className="finding__marks">
          <span className={`finding__severity finding__severity--${status}`}>{statusLabel(status)}</span>
          {impact !== null && impact !== 0 ? (
            <span className="finding__impact">
              {categoryLabel
                ? tx(
                    `${categoryLabel} ${impact > 0 ? '+' : '−'}${Math.abs(impact)} points`,
                    `${categoryLabel} ${impact > 0 ? '+' : '−'}${Math.abs(impact)} puan`,
                    `${categoryLabel} ${impact > 0 ? '+' : '−'}${Math.abs(impact)} pkt`,
                    `${categoryLabel} ${impact > 0 ? '+' : '−'}${Math.abs(impact)} pontos`,
                    `${categoryLabel} ${impact > 0 ? '+' : '−'}${Math.abs(impact)} puntos`,
                  )
                : tx(
                    `${impact > 0 ? '+' : '−'}${Math.abs(impact)} points`,
                    `${impact > 0 ? '+' : '−'}${Math.abs(impact)} puan`,
                    `${impact > 0 ? '+' : '−'}${Math.abs(impact)} pkt`,
                    `${impact > 0 ? '+' : '−'}${Math.abs(impact)} pontos`,
                    `${impact > 0 ? '+' : '−'}${Math.abs(impact)} puntos`,
                  )}
            </span>
          ) : null}
        </p>

        {finding.detail ? <p className="finding__detail">{finding.detail}</p> : null}

        {finding.sourceName ? (
          <p className="finding__source">
            {finding.sourceUrl ? (
              <a href={finding.sourceUrl} target="_blank" rel="noreferrer">
                {finding.sourceName}
                <ExternalLink size={11} aria-hidden="true" style={{ marginLeft: 4 }} />
              </a>
            ) : (
              finding.sourceName
            )}
            {finding.sourceDate ? ` · ${formatDate(finding.sourceDate)}` : ''}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default Finding;
